/**
 * Auto Diff Applier Hook
 * 
 * This hook listens for "diffspec" events from the backend during SSE streams
 * and automatically applies them to the source code files. When the merge
 * succeeds, it sends the merged code back to the backend so the AI can
 * continue working with the updated context.
 * 
 * Usage:
 * ```jsx
 * const { isApplyingDiff, diffProgress } = useAutoDiffApplier({
 *   conversationId,
 *   projectId,
 *   serviceName,
 *   onDiffApplied: (result) => devLog('Diff applied:', result)
 * });
 * ```
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { applyUnifiedDiff, parseUnifiedDiff } from '../utils/diffParser';
import indexedDbFileStorage from '../utils/indexedDbFileStorage';
import AdkClient from '../AdkClient';
import config from '../config';
import { devLog } from '../utils/devLog';

/**
 * Hook to automatically apply diffspecs from the backend
 */
export function useAutoDiffApplier({
  conversationId,
  projectId,
  serviceName,
  sessionId,
  onDiffApplied = null,
  onDiffError = null,
  enabled = true
}) {
  const [isApplyingDiff, setIsApplyingDiff] = useState(false);
  const [diffProgress, setDiffProgress] = useState(null);
  const [lastAppliedDiff, setLastAppliedDiff] = useState(null);
  
  // Track if we're already processing to avoid duplicates
  const processingRef = useRef(false);
  
  /**
   * Send merged code back to AI via API endpoint and chat message
   */
  const sendMergedCodeToAI = useCallback(async (filePath, mergedContent, diffApplied, success, error = null) => {
    try {
      // Skip if no sessionId - can't send message
      if (!sessionId) {
        console.warn('⚠️ No session ID available, skipping AI notification');
        return null;
      }
      
      // Get updated file list from IndexedDB for context update
      let filesInContext = null;
      if (projectId && serviceName) {
        try {
          const allFiles = await indexedDbFileStorage.getAllFiles(projectId, serviceName);
          if (allFiles && allFiles.length > 0) {
            filesInContext = allFiles.map(f => f.filePath);
            devLog(`📂 Including ${filesInContext.length} files in context update`);
          }
        } catch (fileErr) {
          console.warn('⚠️ Failed to get file list for context update:', fileErr);
        }
      }
      
      // Send merged code to backend API endpoint
      devLog(`📡 Sending merged code to backend API`);
      try {
        const apiResponse = await fetch(`${config.agentUrl}/api/diffspec-streaming/merged-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            conversation_id: sessionId,
            file_path: filePath,
            merged_content: mergedContent,
            diff_applied: diffApplied,
            success: success,
            error: error,
            files_in_context: filesInContext
          })
        });
        
        if (!apiResponse.ok) {
          console.error('❌ Failed to send merged code to API:', apiResponse.statusText);
        } else {
          devLog('✅ Merged code sent to backend API successfully');
        }
      } catch (apiErr) {
        console.error('❌ Error calling merged code API:', apiErr);
        // Don't throw - continue to send chat message
      }
      
      // Format message to send to AI
      let messageToAI = '';
      if (success) {
        messageToAI = `✅ Successfully merged changes to \`${filePath}\`\n\n`;
        messageToAI += `<details>\n<summary>Merged File Content (${mergedContent.length} characters)</summary>\n\n`;
        messageToAI += `\`\`\`\n${mergedContent}\n\`\`\`\n\n`;
        messageToAI += `</details>\n\n`;
      } else {
        messageToAI = `❌ Failed to merge changes to \`${filePath}\`\n\nError: ${error}\n\n`;
      }
      
      devLog(`📮 Sending merged code to AI via chat for ${filePath} (${success ? 'success' : 'failed'})`);
      
      // Send as a chat message to the AI
      const sendResult = await AdkClient.sendMessage(
        sessionId,
        messageToAI,
        null, // no abort signal
        null, // no project context
        false, // not canvas mode
        'agent', // agent mode
        null // no attachments
      );
      
      if (!sendResult.success) {
        console.error('❌ Failed to send merged code to AI:', sendResult.message);
        throw new Error(sendResult.message);
      }
      
      devLog('✅ Sent merged code to AI successfully');
      return { success: true };
    } catch (err) {
      console.error('❌ Failed to send merged code to AI:', err);
      throw err;
    }
  }, [sessionId]);
  
  /**
   * Apply a diffspec event
   */
  const applyDiffspecEvent = useCallback(async (event) => {
    // Prevent duplicate processing
    if (processingRef.current) {
      devLog('⏭️  Skipping diffspec - already processing another one');
      return;
    }
    
    processingRef.current = true;
    setIsApplyingDiff(true);
    setDiffProgress({
      stage: 'starting',
      message: `Applying diff to ${event.file_path}...`
    });
    
    try {
      const { file_path, diff_content, request_id } = event;
      
      devLog(`📝 Auto-applying diffspec for: ${file_path}`);
      devLog(`   Diff length: ${diff_content.length} chars`);
      
      // Parse the diff to check operation type
      const parsed = parseUnifiedDiff(diff_content);
      
      if (!parsed.isValid) {
        console.error(`❌ Invalid diff for ${file_path}:`, parsed.error);
        throw new Error(`Invalid diff: ${parsed.error}`);
      }
      
      // Handle file deletion
      if (parsed.isFileDeletion) {
        devLog(`🗑️ Auto-deleting file from IndexedDB: ${file_path}`);
        
        setDiffProgress({
          stage: 'deleting',
          message: `Deleting ${file_path}...`
        });
        
        await indexedDbFileStorage.deleteFile(projectId, serviceName, file_path);
        
        devLog(`✅ File deleted from IndexedDB: ${file_path}`);
        
        // Send deletion notification to AI
        await sendMergedCodeToAI(file_path, '', diff_content, true, null);

        // Update state
        setLastAppliedDiff({
          filePath: file_path,
          success: true,
          deleted: true,
          timestamp: new Date().toISOString()
        });
        
        // Call callback
        if (onDiffApplied) {
          onDiffApplied({
            filePath: file_path,
            deleted: true,
            result: { success: true, operation: 'delete' }
          });
        }
        
        devLog(`🎉 File deletion complete for ${file_path}`);
        return; // Exit early for deletion
      }
      
      // Update progress
      setDiffProgress({
        stage: 'loading',
        message: `Loading current content of ${file_path}...`
      });
      
      // Get current content from IndexedDB
      let currentContent = '';
      const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file_path);
      
      if (stored && stored.content) {
        currentContent = stored.content;
        devLog(`📂 Loaded ${file_path} from IndexedDB (${currentContent.length} chars)`);
      } else {
        devLog(`🆕 No stored content for ${file_path}, using empty base`);
        currentContent = '';
      }
      
      // Update progress
      setDiffProgress({
        stage: 'applying',
        message: `Merging changes to ${file_path}...`
      });
      
      // Apply the diff
      const result = await applyUnifiedDiff(diff_content, currentContent, {
        onProgress: (progress) => {
          setDiffProgress({
            stage: progress.stage || 'applying',
            message: progress.message || progress.text || 'Applying changes...',
            confidence: progress.confidence
          });
        }
      });
      
      if (result.success) {
        const mergedContent = result.newContent;
        
        devLog(`✅ Diff applied successfully to ${file_path}`);
        devLog(`   New content length: ${mergedContent.length} chars`);
        
        // Save merged content to IndexedDB
        setDiffProgress({
          stage: 'saving',
          message: `Saving merged content...`
        });
        
        await indexedDbFileStorage.saveFile(projectId, serviceName, file_path, mergedContent);
        devLog(`💾 Saved merged content to IndexedDB`);
        
        // Send merged code back to backend (with request_id if synchronous)
        setDiffProgress({
          stage: 'syncing',
          message: `Syncing with AI...`
        });
        
        await sendMergedCodeToAI(file_path, mergedContent, diff_content, true, null);

        // Update state
        setLastAppliedDiff({
          filePath: file_path,
          success: true,
          timestamp: new Date().toISOString()
        });
        
        // Call callback
        if (onDiffApplied) {
          onDiffApplied({
            filePath: file_path,
            mergedContent,
            result
          });
        }
        
        devLog(`🎉 Diffspec application complete for ${file_path}`);
        
      } else {
        console.error(`❌ Failed to apply diff to ${file_path}:`, result.error);
        
        // Send failure notification to AI
        await sendMergedCodeToAI(file_path, currentContent, diff_content, false, result.error);
        
        // Dispatch event to prompt user for manual resolution
        window.dispatchEvent(new CustomEvent('diffConflictDetected', {
          detail: {
            filePath: file_path,
            error: result.error,
            conversationId,
            diffContent: diff_content,
            currentContent
          }
        }));
        
        // Update state
        setLastAppliedDiff({
          filePath: file_path,
          success: false,
          error: result.error,
          timestamp: new Date().toISOString(),
          requestId: request_id,
          requiresManualResolution: true
        });
        
        // Call error callback
        if (onDiffError) {
          onDiffError({
            filePath: file_path,
            error: result.error,
            requestId: request_id,
            requiresManualResolution: true
          });
        }
      }
      
    } catch (error) {
      console.error('❌ Error in auto diff application:', error);
      
      // Call error callback
      if (onDiffError) {
        onDiffError({
          error: error.message
        });
      }
      
    } finally {
      processingRef.current = false;
      setIsApplyingDiff(false);
      
      // Clear progress after a delay
      setTimeout(() => {
        setDiffProgress(null);
      }, 2000);
    }
  }, [projectId, serviceName, conversationId, onDiffApplied, onDiffError, sendMergedCodeToAI]);
  
  /**
   * Effect to listen for diffspec events
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }
    
    devLog('👂 Auto diff applier listening for diffspec events');
    
    // Listen for custom diffspec events
    const handleDiffspecEvent = (event) => {
      const eventData = event.detail;
      
      // Only process diffspecs for our conversation
      if (eventData.conversation_id === conversationId || eventData.type === 'diffspec') {
        devLog('🔔 Received diffspec event:', eventData);
        applyDiffspecEvent(eventData);
      }
    };
    
    window.addEventListener('diffspec', handleDiffspecEvent);
    
    return () => {
      window.removeEventListener('diffspec', handleDiffspecEvent);
      devLog('🔇 Auto diff applier stopped listening for diffspec events');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  
  return {
    isApplyingDiff,
    diffProgress,
    lastAppliedDiff
  };
}

export default useAutoDiffApplier;
